import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../contexts/I18nContext';
import { TradeFilters } from '../../components/trades/TradeFilters';
import { AlertCircle, Eye, Repeat, X } from 'lucide-react';
import { TradeCard } from '../../components/trades/TradeCard';
import { PageLayout } from '../../components/layout/PageLayout';
import { Button } from '../../components/ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getRelativeTimeString } from '../../utils/dateUtils';
import { getOptimizedImageUrl, IMAGE_SIZES } from '../../lib/imageUtils';
import { Helmet } from 'react-helmet-async';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { AdSense } from '../../components/ads/AdSense';
import { SEO } from '../../components/seo/SEO';

interface UserCard {
  card_id: number;
  quantity: number;
  user_id: string;
  tradable?: boolean;
  wanted?: boolean;
}

interface Card {
  id: number;
  card_fr: string;
  card_en: string;
  image: string;
  set: string;
  rarity: string;
  user_cards?: UserCard[];
}

interface TradeCard {
  id: string;
  card_id: number;
  card: Card;
}

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  wanted_card: Card;
  trade_cards: TradeCard[];
  creator: {
    username: string;
    avatar_url: string | null;
  };
  set_info?: {
    img: string | null;
    display_name: string;
    display_name_en?: string;
  } | null;
}


interface Filters {
  search: string;
  wantedCardFilter: 'all' | 'owned' | 'tradeable' | 'tradable' | 'wanted';
  proposedCardsFilter: 'all' | 'needAtLeastOne' | 'hasTradeableCard' | 'wanted';
  wantedCardId?: number;
  proposedCardId?: number;
  rarity: string | null;
  wantedCardTradable: boolean;
  proposedCardsWanted: boolean;
}

const sortRarities = (rarities: string[]) => {
  const rarityOrder: { [key: string]: number } = {
    '☆': 0,
    '♢♢♢♢': 1,
    '♢♢♢': 2,
    '♢♢': 3,
    '♢': 4
  };

  return [...rarities].sort((a, b) => {
    return (rarityOrder[a] ?? 999) - (rarityOrder[b] ?? 999);
  });
};

// Composant pour afficher l'image du set ou son nom
const SetImage = ({ setInfo, setName, lang }: { setInfo?: { img: string | null, display_name: string } | null, setName: string, lang: string }) => {
  if (!setInfo) {
    return <p className="text-xs text-gray-500">{setName}</p>;
  }

  if (setInfo.img) {
    const imgUrl = `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${setInfo.img}`;
    return (
      <div className="flex justify-center items-center h-6">
        <img
          src={imgUrl}
          alt={setName}
          className="h-5 w-auto object-contain"
        />
      </div>
    );
  }

  return <p className="text-xs text-gray-500">{setName}</p>;
};

export function BrowseTrades() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, lang, getPathForLang } = useI18n();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTrades, setTotalTrades] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [showHowTo, setShowHowTo] = useState(true);
  const [setsInfo, setSetsInfo] = useState<Map<string, { img: string | null, display_name: string }>>(new Map());
  const tradesPerPage = 21;
  const [rarities, setRarities] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>(() => {
    // Restaurer les filtres depuis sessionStorage s'ils existent
    const savedFilters = sessionStorage.getItem('tradeFilters');
    const savedPage = sessionStorage.getItem('tradePage');

    if (savedPage) {
      setCurrentPage(parseInt(savedPage, 10));
    }

    if (savedFilters) {
      return JSON.parse(savedFilters);
    }

    return {
      search: '',
      wantedCardFilter: 'all',
      proposedCardsFilter: 'all',
      rarity: null,
      wantedCardTradable: false,
      proposedCardsWanted: false
    };
  });

  useEffect(() => {
    if (!authLoading && user) {
      setShowHowTo(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (authLoading) return;
    loadTrades();
  }, [authLoading, filters, currentPage]);

  useEffect(() => {
    const loadEligibleRarities = async () => {
      try {
        const { data: settings } = await supabase
          .from('trading_settings')
          .select('eligible_rarities')
          .single();

        if (settings?.eligible_rarities) {
          setRarities(sortRarities(settings.eligible_rarities));
        }
      } catch (error) {
        console.error('Error loading eligible rarities:', error);
      }
    };

    loadEligibleRarities();
  }, []);

  // Sauvegarder les filtres lorsqu'ils changent
  useEffect(() => {
    sessionStorage.setItem('tradeFilters', JSON.stringify(filters));
  }, [filters]);

  // Sauvegarder la page courante lorsqu'elle change
  useEffect(() => {
    sessionStorage.setItem('tradePage', currentPage.toString());
  }, [currentPage]);

  const loadTrades = async () => {
    setLoading(true);
    try {
      // Construction de la requête de base
      let query = supabase
        .from('trades')
        .select(`
          id,
          status,
          created_at,
          user_id,
          wanted_card:cards!trades_wanted_card_id_fkey!inner(
            id,
            card_fr,
            card_en,
            image,
            set,
            rarity
          ),
          trade_cards:trade_cards!trade_cards_trade_id_fkey(
            id,
            card_id,
            card:cards!trade_cards_card_id_fkey(
              id,
              card_fr,
              card_en,
              image,
              set,
              rarity
            )
          ),
          creator:profiles!trades_user_id_fkey1(
            username,
            avatar_url
          )
        `, { count: 'exact' })
        .eq('status', 'pending');

      // Filtres de base
      if (user?.id) {
        query = query.neq('user_id', user.id);
      }

      if (filters.wantedCardId) {
        query = query.eq('wanted_card_id', filters.wantedCardId);
      }

      // Filtre par rareté (déjà server-side via relation)
      if (filters.rarity) {
        query = query.eq('wanted_card.rarity', filters.rarity);
      }

      // Recherche par texte (sur les noms FR/EN de la carte recherchée)
      if (filters.search.trim()) {
        const normalizedSearch = filters.search.trim().replace(/\s+/g, ' ');
        const searchValue = `%${normalizedSearch}%`;
        const escaped = searchValue.replace(/,/g, '\\,').replace(/\)/g, '\\)');
        query = query.or(
          `wanted_card.card_fr.ilike.${escaped},wanted_card.card_en.ilike.${escaped}`
        );
      }

      // Filtre "Carte proposée" (Recherche par ID)
      if (filters.proposedCardId) {
        const { data: tradeIds } = await supabase
          .from('trade_cards')
          .select('trade_id')
          .eq('card_id', filters.proposedCardId);

        if (tradeIds && tradeIds.length > 0) {
          query = query.in('id', tradeIds.map(t => t.trade_id));
        } else {
          setTrades([]);
          setTotalTrades(0);
          setFilteredTotal(0);
          setLoading(false);
          return;
        }
      }

      // Filtres basés sur les user_cards (Server-side)
      if (user) {
        // 1. Filtre sur la carte recherchée (wanted_card)
        if (filters.wantedCardFilter !== 'all' || filters.wantedCardTradable) {
          let userCardsQuery = supabase
            .from('user_cards')
            .select('card_id')
            .eq('user_id', user.id);

          if (filters.wantedCardFilter === 'owned') {
            userCardsQuery = userCardsQuery.gt('quantity', 0);
          } else if (filters.wantedCardFilter === 'tradeable') {
            userCardsQuery = userCardsQuery.gte('quantity', 3);
          } else if (filters.wantedCardFilter === 'tradable' || filters.wantedCardTradable) {
            userCardsQuery = userCardsQuery.eq('tradable', true);
          }

          const { data: userCardIds } = await userCardsQuery;

          if (userCardIds && userCardIds.length > 0) {
            query = query.in('wanted_card_id', userCardIds.map(uc => uc.card_id));
          } else {
            // Aucun résultat possible si l'utilisateur n'a pas de cartes correspondant au filtre
            setTrades([]);
            setTotalTrades(0);
            setFilteredTotal(0);
            setLoading(false);
            return;
          }
        }

        // 2. Filtre sur les cartes proposées (trade_cards)
        if (filters.proposedCardsFilter === 'wanted' || filters.proposedCardsWanted) {
          // On cherche les cartes que l'utilisateur veut
          const { data: wantedUserCardIds } = await supabase
            .from('user_cards')
            .select('card_id')
            .eq('user_id', user.id)
            .eq('wanted', true);

          if (wantedUserCardIds && wantedUserCardIds.length > 0) {
            // On cherche les trades qui contiennent ces cartes
            const { data: matchingTradeIds } = await supabase
              .from('trade_cards')
              .select('trade_id')
              .in('card_id', wantedUserCardIds.map(uc => uc.card_id));

            if (matchingTradeIds && matchingTradeIds.length > 0) {
              query = query.in('id', matchingTradeIds.map(t => t.trade_id));
            } else {
              setTrades([]);
              setTotalTrades(0);
              setFilteredTotal(0);
              setLoading(false);
              return;
            }
          } else {
            setTrades([]);
            setTotalTrades(0);
            setFilteredTotal(0);
            setLoading(false);
            return;
          }
        }
      }

      // Pagination
      const from = (currentPage - 1) * tradesPerPage;
      const to = from + tradesPerPage - 1;

      query = query
        .order('created_at', { ascending: false })
        .range(from, to);

      const { data: paginatedTrades, count, error } = await query;

      if (error) throw error;

      if (!paginatedTrades) {
        setTrades([]);
        setFilteredTotal(0);
        setTotalTrades(0);
        return;
      }

      // Mise à jour des totaux
      setTotalTrades(count || 0);
      setFilteredTotal(count || 0);

      const tradesData = paginatedTrades as any[];

      // Récupération des sets manquants
      const setNames = new Set<string>();
      tradesData.forEach(trade => {
        const wantedCard = Array.isArray(trade.wanted_card) ? trade.wanted_card[0] : trade.wanted_card;
        if (wantedCard?.set) setNames.add(wantedCard.set);

        trade.trade_cards.forEach((tc: any) => {
          const card = Array.isArray(tc.card) ? tc.card[0] : tc.card;
          if (card?.set) setNames.add(card.set);
        });
      });

      const missingSetNames = Array.from(setNames).filter(setName => !setsInfo.has(setName));
      if (missingSetNames.length > 0) {
        const { data: setsData } = await supabase
          .from('sets')
          .select('name, img, display_name')
          .in('name', missingSetNames);

        if (setsData) {
          const newSetsInfo = new Map(setsInfo);
          setsData.forEach(set => {
            newSetsInfo.set(set.name, { img: set.img, display_name: set.display_name });
          });
          setSetsInfo(newSetsInfo);
        }
      }

      // Récupération des user_cards pour les trades affichés
      let userCardsMap = new Map<number, UserCard>();
      if (user) {
        const cardIds = new Set<number>();
        tradesData.forEach(trade => {
          const wantedCard = Array.isArray(trade.wanted_card) ? trade.wanted_card[0] : trade.wanted_card;
          if (wantedCard?.id) cardIds.add(wantedCard.id);

          trade.trade_cards.forEach((tc: any) => {
            const card = Array.isArray(tc.card) ? tc.card[0] : tc.card;
            if (card?.id) cardIds.add(card.id);
          });
        });

        if (cardIds.size > 0) {
          const { data: myCards } = await supabase
            .from('user_cards')
            .select('*')
            .eq('user_id', user.id)
            .in('card_id', Array.from(cardIds));

          if (myCards) {
            userCardsMap = new Map(myCards.map(uc => [uc.card_id, uc as UserCard]));
          }
        }
      }

      // Enrichissement des trades
      let enrichedTrades = tradesData.map(trade => {
        const rawWantedCard = Array.isArray(trade.wanted_card) ? trade.wanted_card[0] : trade.wanted_card;
        const rawCreator = Array.isArray(trade.creator) ? trade.creator[0] : trade.creator;

        const wantedCard: Card = {
          ...rawWantedCard,
          user_cards: user ? [userCardsMap.get(rawWantedCard.id)].filter(Boolean) as UserCard[] : []
        };

        const tradeCards: TradeCard[] = trade.trade_cards
          .filter((tc: any) => tc && (Array.isArray(tc.card) ? tc.card[0] : tc.card))
          .map((tc: any) => {
            const rawCard = Array.isArray(tc.card) ? tc.card[0] : tc.card;
            return {
              ...tc,
              card: {
                ...rawCard,
                user_cards: user ? [userCardsMap.get(rawCard.id)].filter(Boolean) as UserCard[] : []
              }
            };
          });

        return {
          ...trade,
          wanted_card: wantedCard,
          trade_cards: tradeCards,
          creator: rawCreator,
          set_info: rawWantedCard?.set ? setsInfo.get(rawWantedCard.set) : null
        } as Trade;
      });

      // Application des filtres client-side restants
      // Application des filtres client-side restants (uniquement ceux qui sont difficiles à faire server-side)
      if (user) {
        if (filters.proposedCardsFilter !== 'all' && filters.proposedCardsFilter !== 'wanted') {
          enrichedTrades = enrichedTrades.filter(trade => {
            switch (filters.proposedCardsFilter) {
              case 'needAtLeastOne':
                return trade.trade_cards.some(tc => {
                  const userCard = tc.card.user_cards?.[0];
                  return !userCard || userCard.quantity === 0;
                });
              case 'hasTradeableCard':
                return trade.trade_cards.some(tc => {
                  const userCard = tc.card.user_cards?.[0];
                  return !userCard || userCard.quantity <= 1;
                });
              default:
                return true;
            }
          });
        }
      }

      setTrades(enrichedTrades);

    } catch (error) {
      console.error('Error loading trades:', error);
      setTrades([]);
      setFilteredTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTrade = (tradeId: string) => {
    // Sauvegarder les filtres et la page actuelle avant de naviguer
    sessionStorage.setItem('tradeFilters', JSON.stringify(filters));
    sessionStorage.setItem('tradePage', currentPage.toString());

    const path = `/ trades / ${tradeId}/info`;
    navigate(getPathForLang(path, lang));
  };

  const handleSearch = (search: string) => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      search: search.trim()
    }));
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  const totalPages = Math.ceil(filteredTotal / tradesPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Ajouter une fonction pour réinitialiser tous les filtres
  const handleResetFilters = () => {
    const defaultFilters: Filters = {
      search: '',
      wantedCardFilter: 'all' as const,
      proposedCardsFilter: 'all' as const,
      rarity: null,
      wantedCardTradable: false,
      proposedCardsWanted: false
    };

    setFilters(defaultFilters);
    sessionStorage.removeItem('tradeFilters');
    sessionStorage.removeItem('tradePage');
    setCurrentPage(1);
  };

  if (authLoading || loading) {
    return (
      <>
        <SEO
          title={t('trades.seo.title')}
          description={t('trades.seo.description')}
          og_title={t('trades.seo.og_title')}
          og_description={t('trades.seo.og_description')}
        />
        <PageLayout
          breadcrumbProps={{
            items: [
              { label: 'Échanges', path: '/trades/browse', icon: <Repeat className="w-4 h-4" aria-hidden="true" /> }
            ]
          }}
        >
          <div className="max-w-7xl mx-auto px-0 sm:px-4 py-4 sm:py-8">
            <header className="mb-8 px-4 sm:px-0">
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-4"></div>
              <div className="grid gap-2">
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </header>

            <section className="mb-8 px-4 sm:px-0">
              <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="w-12 h-12 bg-gray-200 rounded-full mb-4 animate-pulse"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-3"></div>
                    <div className="space-y-2">
                      <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-8 px-4 sm:px-0">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 -mx-4 sm:mx-0">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <div className="space-y-2">
                    <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mt-4 lg:mt-0"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </section>

            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-28">
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                      </div>

                      <div className="aspect-[3/4] bg-gray-200 rounded-lg animate-pulse mb-3"></div>
                      <div className="space-y-2">
                        <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-3 w-2/3 bg-gray-200 rounded animate-pulse mx-auto"></div>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-end mb-2">
                        <div className="h-4 w-24 bg-gray-200 rounded-full animate-pulse"></div>
                      </div>

                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {[...Array(6)].map((_, cardIndex) => (
                            <div key={cardIndex} className="aspect-[3/4] bg-gray-200 rounded-lg animate-pulse"></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <nav className="mt-6 border-t border-gray-200 pt-6 px-4 sm:px-0">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                <div className="flex items-center gap-2">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t('trades.seo.title')}
        description={t('trades.seo.description')}
        og_title={t('trades.seo.og_title')}
        og_description={t('trades.seo.og_description')}
        includeAdsense={true}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": t('trades.seo.structured_data.name'),
          "description": t('trades.seo.structured_data.description'),
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": filteredTotal,
            "itemListElement": trades.map((trade, index) => ({
              "@type": "Thing",
              "position": index + 1,
              "name": t('trades.seo.structured_data.trade_item_name', {
                card: lang === 'en' ? trade.wanted_card.card_en : trade.wanted_card.card_fr
              }),
              "description": t('trades.seo.structured_data.trade_item_description', {
                card: lang === 'en' ? trade.wanted_card.card_en : trade.wanted_card.card_fr
              }),
              "additionalType": "VirtualGameItem",
              "category": t('trades.seo.structured_data.category')
            }))
          }
        }}
      />
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('trades.breadcrumb.trades'), path: '/trades/browse', icon: <Repeat className="w-4 h-4" aria-hidden="true" /> }
          ]
        }}
      >
        <main className="max-w-7xl mx-auto px-0 sm:px-4 py-4 sm:py-8">
          <header className="mb-8 px-4 sm:px-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('trade_choice.seo.title')}</h1>
            {!user && (
              <div className="prose prose-sm max-w-none text-gray-500">
                <p className="mb-4">
                  {t('trade_choice.seo.description')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 text-sm">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('trade_info.how_trade_works')}</h3>
                    <p>{t('trade_info.step1')}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('trades.stats.total')}</h3>
                    <p>{t('trade_choice.browse_trades.description')}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{t('trade_info.step2')}</h3>
                    <p>{t('trade_info.step3')}</p>
                  </div>
                </div>
              </div>
            )}
          </header>

          <section className="mb-8 px-4 sm:px-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('trade_info.how_trade_works')}</h2>
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHowTo(!showHowTo)}
                  className="text-gray-600"
                >
                  {showHowTo ? t('common.cancel') : t('trade_info.how_trade_works')}
                </Button>
              )}
            </div>
            {showHowTo && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-full mb-4">
                      <span className="text-xl font-bold text-primary-600">1</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('trade_info.step1')}</h3>
                    <p className="text-gray-600">
                      {t('trade_choice.browse_trades.description')}
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-full mb-4">
                      <span className="text-xl font-bold text-primary-600">2</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('trade_info.step2')}</h3>
                    <p className="text-gray-600">
                      {t('trade_info.step3')}
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-full mb-4">
                      <span className="text-xl font-bold text-primary-600">3</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('trade_info.step4')}</h3>
                    <p className="text-gray-600">
                      {t('trade_info.token_warning')}
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-full mb-4">
                      <span className="text-xl font-bold text-primary-600">4</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('trade_info.accept_trade')}</h3>
                    <p className="text-gray-600">
                      {t('trade_info.accept_terms')}
                    </p>
                  </div>
                </div>
                {!user && (
                  <div className="mt-6 bg-primary-50 p-4 rounded-lg text-center">
                    <p className="text-gray-700">
                      {t('trade_info.login_required')}{' '}
                      <Link to={getPathForLang("/login", lang)} className="text-primary-600 hover:text-primary-700 font-semibold">
                        {t('trade_info.login')}
                      </Link>
                    </p>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="mb-8 px-4 sm:px-0" aria-label={t('trades.list.aria_label')}>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 -mx-4 sm:mx-0">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('trade_choice.browse_trades.title')}</h2>
                  <p className="text-gray-600">{t('trade_choice.browse_trades.description')}</p>
                </div>
                <div className="mt-4 lg:mt-0 flex flex-col sm:flex-row items-center gap-3">
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('public_decks.filters.reset_all_filters')}
                  </button>
                  <span className="text-sm text-gray-500">
                    {filteredTotal} {t('trades.stats.total')}
                  </span>
                </div>
              </div>
              <div className="w-full">
                <TradeFilters
                  filters={filters as any}
                  onFilterChange={handleFilterChange as any}
                  onSearch={handleSearch}
                  isAuthenticated={!!user}
                  isProposedCardsSection={true}
                  rarities={rarities}
                />
              </div>
            </div>
          </section>

          {trades.length === 0 ? (
            <section aria-label={t('trades.list.aria_label')} className="mx-0 sm:mx-0 text-center py-8 bg-white shadow-sm sm:rounded-lg">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
              <p className="text-gray-500">{t('trades.empty.created.title')}</p>
              {!user && (
                <p className="mt-4 text-sm text-gray-600">
                  <Link to={getPathForLang("/login", lang)} className="text-primary-600 hover:text-primary-700">
                    {t('trade_info.login')}
                  </Link>{' '}
                  {t('trade_info.login_required')}
                </p>
              )}
            </section>
          ) : (
            <>
              <section
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fadeIn"
                aria-label={t('trades.list.aria_label')}
              >
                {/* Annonce AdSense en haut de la grille des trades */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center" style={{ minHeight: '250px', width: '100%' }}>
                  <AdSense
                    adSlot="1424794127"
                    id="trades-top"
                    className="w-full flex justify-center items-center"
                    style={{ minHeight: '250px' }}
                  />
                </div>

                {trades.map((trade, index) => {
                  // Afficher une annonce tous les 8 trades au lieu de 15 pour augmenter la fréquence sur mobile
                  const showAd = index > 0 && index % 8 === 0 && index < trades.length - 1;

                  return (
                    <React.Fragment key={trade.id}>
                      {showAd && (
                        <div className="col-span-1 sm:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center" style={{ minHeight: '250px', width: '100%' }}>
                          <AdSense
                            adSlot="1424794127"
                            id={`trades-middle-${index}`}
                            className="w-full flex justify-center items-center"
                            style={{ minHeight: '250px' }}
                          />
                        </div>
                      )}
                      <div
                        className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => handleViewTrade(trade.id)}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-28">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-2">
                                  {trade.creator.avatar_url ? (
                                    <Link
                                      to={`/users/${trade.creator.username}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="block"
                                    >
                                      <img
                                        src={trade.creator.avatar_url}
                                        alt={`Avatar de ${trade.creator.username}`}
                                        className="w-8 h-8 rounded-full hover:ring-2 hover:ring-primary-300 transition-all"
                                      />
                                    </Link>
                                  ) : (
                                    <Link
                                      to={`/users/${trade.creator.username}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="block"
                                    >
                                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center hover:ring-2 hover:ring-primary-300 transition-all">
                                        <span className="text-primary-600 font-semibold">
                                          {trade.creator.username.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                    </Link>
                                  )}
                                  <Link
                                    to={`/users/${trade.creator.username}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-sm font-medium text-gray-900 truncate max-w-[100px] hover:text-primary-600 transition-colors"
                                    title={trade.creator.username}
                                  >
                                    {trade.creator.username}
                                  </Link>
                                </div>
                              </div>

                              <div className="flex justify-center mb-3">
                                <div className="aspect-[3/4]">
                                  <img
                                    src={getOptimizedImageUrl(trade.wanted_card.image, IMAGE_SIZES.THUMBNAIL).replace('https://image.pokemonpocket.fr/', `https://image.pokemonpocket.fr/${lang === 'en' ? 'en/' : ''}preview/`)}
                                    alt={lang === 'en' ? trade.wanted_card.card_en : trade.wanted_card.card_fr}
                                    className="w-full h-full object-contain rounded-lg"
                                    loading="lazy"
                                  />
                                </div>
                              </div>

                              <div className="text-center">
                                <h3 className="text-sm font-medium text-gray-900">
                                  {lang === 'en' && trade.wanted_card.card_en ? trade.wanted_card.card_en : trade.wanted_card.card_fr}
                                </h3>
                                <SetImage
                                  setInfo={setsInfo.get(trade.wanted_card.set)}
                                  setName={trade.wanted_card.set}
                                  lang={lang}
                                />
                              </div>
                            </div>

                            <div className="flex-1">
                              <div className="text-right mb-2">
                                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                                  {getRelativeTimeString(new Date(trade.created_at), t)}
                                </span>
                              </div>

                              <div>
                                <h4 className="text-xs font-medium text-gray-500 mb-2">{t('trades.browse.offered_cards')}</h4>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 grid-rows-[repeat(2,_minmax(0,_1fr))]">
                                  {trade.trade_cards.slice(0, trade.trade_cards.length > 6 ? 5 : 6).map((tc) => (
                                    <div key={tc.id} className="relative group">
                                      <img
                                        src={getOptimizedImageUrl(tc.card.image, IMAGE_SIZES.THUMBNAIL).replace('https://image.pokemonpocket.fr/', `https://image.pokemonpocket.fr/${lang === 'en' ? 'en/' : ''}preview/`)}
                                        alt={lang === 'en' && tc.card.card_en ? tc.card.card_en : tc.card.card_fr}
                                        className="w-full aspect-[3/4] object-contain rounded-lg"
                                        loading="lazy"
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                                        <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-1">
                                          {lang === 'en' && tc.card.card_en ? tc.card.card_en : tc.card.card_fr}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                  {trade.trade_cards.length > 6 && (
                                    <div className="relative flex items-center justify-center bg-gray-100 aspect-[3/4] rounded-lg">
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-lg">
                                        <span className="text-gray-700 text-sm">
                                          {t('trades.browse.more_cards', { count: trade.trade_cards.length - 5 })}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="mt-3">
                                  <Link
                                    to={getPathForLang(`/trades/${trade.id}/info`, lang)}
                                    className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    {t('trades.browse.view_details')}
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                {/* Afficher une annonce à la fin si on a moins de 3 annonces intermédiaires */}
                {trades.length > 0 && trades.length > 8 && Math.floor((trades.length - 1) / 8) < 3 && (
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-center" style={{ minHeight: '250px', width: '100%' }}>
                    <AdSense
                      adSlot="1424794127"
                      id="trades-bottom"
                      className="w-full flex justify-center items-center"
                      style={{ minHeight: '250px' }}
                    />
                  </div>
                )}
              </section>

              <nav
                className="mt-6 border-t border-gray-200 pt-6 px-4 sm:px-0"
                aria-label={t('trades.list.aria_label')}
              >
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-700 whitespace-nowrap">
                    <span className="font-medium">{trades.length > 0 ? (currentPage - 1) * tradesPerPage + 1 : 0}</span>
                    {' - '}
                    <span className="font-medium">{Math.min(currentPage * tradesPerPage, filteredTotal)}</span>
                    {' '}{t('trades.stats.total')}{' '}
                    <span className="font-medium">{filteredTotal}</span>
                    {filteredTotal !== totalTrades && (
                      <span className="text-gray-500">
                        ({t('trades.stats.total')} {totalTrades})
                      </span>
                    )}
                  </div>

                  <nav className="flex items-center gap-1 sm:gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="hidden sm:flex items-center gap-1"
                      title={t('common.previous')}
                    >
                      <ChevronLeft className="h-3 w-3" />
                      <ChevronLeft className="h-3 w-3 -ml-2" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex items-center"
                      title={t('common.previous')}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">{t('common.previous')}</span>
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (totalPages <= 7) return true;
                          const distance = Math.abs(page - currentPage);
                          return distance === 0 || distance === 1 || page === 1 || page === totalPages;
                        })
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="px-2 text-gray-500">•••</span>
                            )}
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className={`min-w-[2.5rem] ${currentPage === page ? 'font-semibold' : ''}`}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="flex items-center"
                      title={t('common.next')}
                    >
                      <span className="hidden sm:inline mr-1">{t('common.next')}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="hidden sm:flex items-center gap-1"
                      title={t('common.next')}
                    >
                      <ChevronRight className="h-3 w-3" />
                      <ChevronRight className="h-3 w-3 -ml-2" />
                    </Button>
                  </nav>
                </div>
              </nav>
            </>
          )}
        </main>
      </PageLayout>
    </>
  );
} 